import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Typography,
  Modal,
  MenuItem,
  Select,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from "material-react-table";
import { Edit } from "@mui/icons-material";
import Accordion from "react-bootstrap/Accordion";
import { Container, Row } from "react-bootstrap";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Download from "@mui/icons-material/Download";
import axiosInstance from "../../../../Utils/axiosInstance";
import { toast } from "react-toastify";
import CheckCircle from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArticleIcon from "@mui/icons-material/Article";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import FilterNoneIcon from "@mui/icons-material/FilterNone";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DraggablePaper from "../../../../Utils/CommonComponents/Dragging/Draggable";
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
interface FormData {
  book: string;
  strategy: string;
  price: string;
  broker: string;
}
type Touched = {
  [key: string]: boolean;
};

const IceExhcnageTrades = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editingRows, setEditingRows] = useState<any[]>([]);
  const [formData, setFormData] = useState<FormData>({
    book: "",
    strategy: "",
    price: "",
    broker: "",
  });
  const [toastOpen, setToastOpen] = useState(false);
  const [books, setBooks] = useState<any[]>([]);
  const [strategies, setStrategies] = useState<any[]>([]);
  const [brokers, setBrokers] = useState<any[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkDeleteIds, setBulkDeleteIds] = useState<string[]>([]);
  const [openBulkDeleteDialog, setOpenBulkDeleteDialog] = useState(false);
  const [originalData, setOriginalData] = useState<any>({});
  const [touched, setTouched] = useState({
    book: false,
    strategy: false,
    price: false,
    broker: false,
  });
  const [openLogModal, setOpenLogModal] = useState(false);
  const [activeSource, setActiveSource] = useState("ICE"); // default tab
  const [logText, setLogText] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [maximized, setMaximized] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState<{
    status: "success" | "warning" | "error";
    message: string;
    created: string[];
    skipped: string[];
  } | null>(null);
  const [resultMessage, setResultMessage] = useState("");
  const [openRawModal, setOpenRawModal] = useState(false);
  const [selectedRawMessage, setSelectedRawMessage] = useState<string>("");
  const [isMaximized, setIsMaximized] = useState(false);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const handleOpenBulkDeleteDialog = (ids: string[]) => {
    if (!ids.length) return toast.info("Please select at least one row!");
    console.log("🧾 Selected Trade IDs:", ids);
    setBulkDeleteIds(ids);
    setOpenBulkDeleteDialog(true);
  };

  const handleCloseBulkDeleteDialog = () => {
    setOpenBulkDeleteDialog(false);
    setBulkDeleteIds([]);
  };

  const handleOpenConfirm = () => setConfirmOpen(true);
  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setProcessResult(null);
  };

  const handleConfirmProcess = async () => {
    await handleProcess();
  };

  const HIDDEN_FIELDS = [
    "id",
    "ordering_id",
    "is_deleted",
    "deleted_at",
    "created_at",
    "retrieved_at",
  ];

  const COLUMN_ORDER = [
    "date",
    "TransactTime",
    "Trade_id",
    "LegRefID",
    "trader",
    "derivatives",
    "volume",
    "symbol_code",
    "Market_ID",
    "contract",
    "start_date",
    "end_date",
    "price",
    "unit",
    "type",
    "marker_tas_type",
    "Blocked",
    "ExecType",
    "OrdStatus",
    "api_company_name",
    "parent_company",
    "clearer",
    "clearing_acct",
    "broker",
    "book",
    "strategy",
    "notes",
    "created_date",
    "updated_at",
    "exchange_type",
    "platform",
  ];

  const COLUMN_LABELS: Record<string, string> = {
    date: "Date",
    TransactTime: "Transact Time",
    Trade_id: "Trade ID",
    LegRefID: "LegRef ID",
    trader: "Trader",
    derivatives: "Derivatives",
    volume: "Quantity(lots)",
    symbol_code: "Symbol Code",
    Market_ID: "Market ID",
    contract: "Contract",
    start_date: "Pricing From",
    end_date: "Pricing To",
    price: "Price",
    unit: "Unit",
    type: "Type",
    marker_tas_type: "Marker/TAS Type",
    Blocked: "Blocked",
    ExecType: "Exec Type",
    OrdStatus: "Order Status",
    api_company_name: "API Company Name",
    parent_company: "Parent Company",
    clearer: "Clearer",
    clearing_acct: "Clearing Account",
    broker: "Broker",
    book: "Book",
    strategy: "Strategy",
    notes: "Notes",
    created_date: "Created Date",
    updated_at: "Updated At",
    exchange_type: "Exchange Type",
    platform: "Platform",
  };

  const fetchData = async () => {
    try {
      const res = await axiosInstance.get(
        "/paper-app/api/clearing-account-trades/"
      );
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setRows(data);
    } catch (error) {
      console.error("Error fetching trade data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const fetchDropdowns = async () => {
      try {
        const [booksRes, strategiesRes, brokersRes] = await Promise.all([
          axiosInstance.get("/paper-app/api-form-book/"),
          axiosInstance.get("/paper-app/api-form-strategy/"),
          axiosInstance.get("/paper-app/api-form-brokers/"),
        ]);
        setBooks(booksRes.data);
        setStrategies(strategiesRes.data);
        setBrokers(brokersRes.data);
      } catch (error) {
        console.error("Error fetching dropdowns:", error);
      }
    };
    fetchDropdowns();
  }, []);

  const fetchLogs = async (source: string) => {
    try {
      const res = await axiosInstance.get(`/paper-app/logs/?source=${source}`);
      setLogText(res.data?.log_data || "No logs found");
    } catch (err) {
      console.error(err);
      toast.error("Failed to load logs");
    }
  };

  const handleOpenLogs = () => {
    setOpenLogModal(true);
    fetchLogs("ICE");
  };

  const handleTabChange = (src: string) => {
    setActiveSource(src);
    fetchLogs(src);
  };

  const filteredLogs = logText
    .split("\n")
    .filter((line) => line.toLowerCase().includes(searchTerm.toLowerCase()))
    .join("\n");

  const handleProcess = async () => {
    setProcessing(true);
    try {
      const selectedRows = table
        .getSelectedRowModel()
        .flatRows.map((r) => r.original);

      if (!selectedRows.length) {
        toast.warn("Please select at least one trade to process.");
        setProcessing(false);
        return;
      }

      const rowsToSkip: number[] = [];
      const rowsToProcess: any[] = [];

      selectedRows.forEach((row, index) => {
        const hasRestricted = Object.values(row).some(
          (value) =>
            typeof value === "string" &&
            value.toLowerCase().includes("please")
        );

        if (hasRestricted) {
          rowsToSkip.push(index + 1);
        } else {
          rowsToProcess.push(row);
        }
      });

      if (!rowsToProcess.length) {
        setProcessResult({
          status: "warning",
          message: `Cannot process selected trades. Skipped rows: ${rowsToSkip.join(", ")}`,
          created: [],
          skipped: rowsToSkip.map((idx) => `Row ${idx}`),
        });
        setProcessing(false);
        return;
      }

      const selectedIds = rowsToProcess
        .map((r) => r.Trade_id || r.trade_id)
        .filter(Boolean);

      const res = await axiosInstance.post("/paper-app/process-api-trades/", {
        Trade_ids: selectedIds,
      });

      const { status, message, created = [], skipped = [] } = res.data;

      const isAllSkipped =
        message === "All trades are skipped." ||
        (created.length === 0 && skipped.length > 0);

      const finalStatus = isAllSkipped ? "warning" : status;

      const createdDetails = isAllSkipped ? [] : created;


      // Map skipped items for display
      const skippedDetails =
        skipped.length > 0
          ? skipped
          : rowsToSkip.length > 0
            ? rowsToSkip.map((idx) => `Row ${idx}`)
            : [];

      setProcessResult({
        status: finalStatus,
        message,
        created: createdDetails,
        skipped: skippedDetails,
      });

      if (status === "success") {
        // Remove processed trades from table
        if (created.length > 0) {
          setRows((prev) =>
            prev.filter((row) => !selectedIds.includes(row.Trade_id))
          );
        }
        table.resetRowSelection();
      }
    } catch (error: any) {
      console.error("Process API Error:", error);

      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to process trades. Please try again.";

      setProcessResult({
        status: "error",
        message: errorMsg,
        created: [],
        skipped: [],
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleExportLogs = () => {
    const textToExport = filteredLogs || logText;

    if (!textToExport) {
      toast.error("No logs to export");
      return;
    }

    const blob = new Blob([textToExport], { type: "text/plain" });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
    link.download = `${activeSource}_logs_${timestamp}.txt`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success("Logs exported successfully");
  };

  const isAllSkipped =
    processResult &&
    processResult.created.length === 0 &&
    processResult.skipped.length > 0;

  const finalStatus = isAllSkipped
    ? "failed"
    : processResult?.status;


  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    if (rows.length === 0) return [];

    const dynamicColumns = COLUMN_ORDER.map((key) => ({
      accessorKey: key,
      header: COLUMN_LABELS[key] || key,
      size: 140,
      enableColumnFilter: true,
      enableGrouping: true,
      enablePinning: true,
      enableColumnActions: true,
      GroupedCell: ({ cell }: any) => {
        const value = cell.getValue();
        const count = cell.row.subRows?.length || 0;
        return (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
              fontWeight: 600,
            }}
          >
            {value} <span style={{ opacity: 0.7 }}>({count})</span>
          </span>
        );
      },

      // your normal cell renderer
      Cell: ({ cell }: any) => {
        const value = cell.getValue();
        const isPlease =
          typeof value === "string" &&
          (value.toLowerCase().startsWith("please") ||
            value.toLowerCase().includes("please"));

        return (
          <span
            style={{
              whiteSpace: "nowrap",
              color: isPlease ? "red" : "inherit",
              fontWeight: isPlease ? 500 : "normal",
            }}
          >
            {value ?? "-"}
          </span>
        );
      },
    }));

    const rawMessageColumn: MRT_ColumnDef<any> = {
      accessorKey: "raw_message",
      header: "Raw Message",
      size: 120,
      enableColumnFilter: false,
      enableGrouping: false,
      enableColumnActions: false,
      Cell: ({ row }) => {
        const message = row.original.raw_message;

        if (!message) return "-";

        return (
          <span
            style={{
              color: "#1976d2",
              cursor: "pointer",
              textDecoration: "underline",
              fontWeight: 500,
            }}
            onClick={() => handleOpenRawModal(message)}
          >
            Message
          </span>
        );
      },
    };
 

    const actionsColumn: MRT_ColumnDef<any> = {
      id: "actions",
      header: "Actions",
      size: 120,
      minSize: 1,
      enableResizing: true,
      enablePinning: true, // ✅ Important: so it can stay pinned
      enableColumnActions: false, // (optional — hide menu for this column)
      muiTableHeadCellProps: {
        sx: {
          whiteSpace: "nowrap",
          textAlign: "center",
          fontWeight: 600,
        },
      },
      muiTableBodyCellProps: {
        sx: { textAlign: "center" },
      },
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: "4px", justifyContent: "center" }}>
          <Tooltip title="Edit">
            <IconButton
              sx={{ color: "gray" }}
              size="small"
              onClick={() => handleOpenModal([row.original])}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={() =>
                handleOpenBulkDeleteDialog([row.original.Trade_id])
              }
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    };

    return [actionsColumn, ...dynamicColumns, rawMessageColumn];
  }, [rows]);

  const handleConfirmBulkDelete = async () => {
    try {
      const res = await axiosInstance.delete(
        "/paper-app/api-pending-trades/select-delete/",
        {
          data: { Trade_ids: bulkDeleteIds }, // ✅ Backend expects this key
        }
      );

      if (res.status === 200) {
        toast.success("Selected trades deleted successfully!");

        // ✅ Remove deleted rows from UI
        setRows((prev) =>
          prev.filter((r) => !bulkDeleteIds.includes(r.Trade_id))
        );

        // ✅ Clear selection after successful delete
        table.resetRowSelection();

        // ✅ Close dialog
        handleCloseBulkDeleteDialog();
      } else {
        toast.info("Some trades could not be deleted.");
      }
    } catch (error: any) {
      console.error("Bulk delete error:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete selected trades."
      );
    } finally {
      // ensure dialog closes in any case
      handleCloseBulkDeleteDialog();
    }
  };

  const handleBulkEdit = (selectedRows: any[]) => {
    if (selectedRows.length === 0) return;
    handleOpenModal(selectedRows);
  };

  const handleOpenModal = (rowsToEdit: any[]) => {
    setEditingRows(rowsToEdit);

    const row = rowsToEdit[0]; // first row, even in bulk edit

    // Store original values for comparison
    const orig = {
      book: row?.book || "",
      strategy: row?.strategy || "",
      price: row?.price || "",
      broker: row?.broker || "",
    };

    setOriginalData(orig);

    // Fill form fields with the same initial values
    setFormData(orig);

    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleOpenRawModal = (message: string) => {
    setSelectedRawMessage(message);
    setOpenRawModal(true);
  };

  const handleCloseRawModal = () => {
    setOpenRawModal(false);
    setIsMaximized(false);
  };

  // Convert ID -> Name
  const getLabel = (list: any[], id: string) =>
    list?.find((x) => x.id === id)?.name || "";

  const handleSubmit = async () => {
    try {
      // --- Collect IDs ---
      const Trade_ids = editingRows
        ?.map((row) => row.Trade_id || row.trade_id)
        .filter(Boolean);

      if (!Trade_ids?.length) {
        toast.error("No Trade IDs found");
        return;
      }

      // --- Build payload ONLY with changed fields ---
      const payload: any = { Trade_ids };

      if (formData.book !== originalData.book)
        payload.book_name = formData.book;

      if (formData.strategy !== originalData.strategy)
        payload.strategy_name = formData.strategy;

      if (formData.broker !== originalData.broker)
        payload.broker_name = formData.broker;

      if (formData.price !== originalData.price)
        payload.price = Number(formData.price);

      // --- If No Changes ---
      if (Object.keys(payload).length === 1) {
        toast.warn("No changes detected.");
        return;
      }

      console.log("Submitting changed fields:", payload);

      // --- Submit API ---
      const res = await axiosInstance.post(
        "/paper-app/update-select-api/",
        payload
      );

      if (res?.status === 200) {
        toast.success("Trades updated successfully!");

        // --- 🔥 Local table update (No fetchData) ---
        setRows((prevRows) =>
          prevRows.map((row) => {
            if (Trade_ids.includes(row.Trade_id || row.trade_id)) {
              return {
                ...row,

                // 👇 Convert ID → Label before updating UI
                ...(payload.book_name && {
                  book: getLabel(books, payload.book_name),
                }),

                ...(payload.strategy_name && {
                  strategy: getLabel(strategies, payload.strategy_name),
                }),

                ...(payload.broker_name && {
                  broker: getLabel(brokers, payload.broker_name),
                }),

                ...(payload.price && { price: payload.price }),
              };
            }
            return row;
          })
        );

        handleCloseModal();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Update failed");
      console.error(error);
    }
  };

  const compactInputSx = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      height: 30,
      fontSize: "0.8rem",
      borderRadius: "4px", // pure square
      backgroundColor: "#fff",
      border: "1px solid #cfd8dc",
      transition: "all 0.15s ease-in-out",

      "&:hover": {
        borderColor: "#90a4ae",
        backgroundColor: "#f9f9f9",
      },
      "&.Mui-focused": {
        borderColor: "#009db5",
        backgroundColor: "#fff",
        boxShadow: "0 0 0 2px rgba(0,157,181,0.15)",
      },
      "& fieldset": { border: "none" },
    },
    // Ensures consistent padding & alignment across Select & TextField
    "& .MuiSelect-select, & .MuiInputBase-input": {
      px: 1.2,
      py: 0.3,
      fontSize: "0.8rem",
      display: "flex",
      alignItems: "center",
    },
    // Dropdown icon styling
    "& .MuiSelect-icon": {
      color: "#90a4ae",
      fontSize: "1rem",
      right: 8,
    },
  };
  // useEffect(() => {
  //   const tableEl = tableContainerRef.current;
  //   if (!tableEl) return;

  //   const observer = new MutationObserver(() => {
  //     tableEl.scrollTop = 0; // reset scroll to top whenever table updates
  //   });

  //   observer.observe(tableEl, { childList: true, subtree: true });

  //   return () => observer.disconnect();
  // }, [rows]); // run whenever rows change (page size or page change)

  const table = useMaterialReactTable({
    columns,
    data: rows,
    enableColumnPinning: true, // ✅ master switch for pinning
    enableColumnActions: true,
    enableRowSelection: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableDensityToggle: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enablePagination: true,
    enableFullScreenToggle: true,
    enableStickyHeader: true,
    enableColumnResizing: true, // ✅ Added
    enableRowNumbers: true,
    enableHiding: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnDragging: true,
    // enableGlobalFilter: false,
    enableSorting: true,
    layoutMode: "grid",
    initialState: {
      columnPinning: {
        left: ["actions"],
      },
      pagination: {
        pageSize: 20, // 👈 rows per page initially
        pageIndex: 0,
      },
    },
    muiTableProps: {
      sx: {
        tableLayout: "auto !important", // ⬅️ Prevent forced fixed layout
      },
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        enableColumnActions: true,
        size: 130, //set custom width
        muiTableHeadCellProps: {
          // align: "center", //change head cell props
        },
      },
      "mrt-row-numbers": {
        enableColumnDragging: false,
        enableColumnOrdering: true, //turn on some features that are usually off
        // enableResizing: true,
        muiTableHeadCellProps: {
          sx: {
            fontSize: "1.2rem",
          },
        },
      },
      "mrt-row-select": {
        enableColumnActions: true,
        enableHiding: true,
        // size: 50,
      },
    },
    muiTableBodyCellProps: {
      sx: {
        whiteSpace: "normal !important",
        wordBreak: "break-word",
        lineHeight: "0.9", // ✅ Super tight
        padding: "0px 3px", // ✅ Extra minimal
        verticalAlign: "middle !important",
        minHeight: "25px", // ✅ Very compact
        height: "auto",
        maxHeight: "none",
        fontSize: "2.5rem",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        whiteSpace: "normal !important",
        wordBreak: "break-word",
        padding: "6px 10px",
        fontSize: "0.7rem",
        textAlign: "center",
        "& .Mui-TableHeadCell-ResizeHandle": {
          backgroundColor: "#ddd",
          width: "3px",
          borderRadius: "2px",
          "&:hover": {
            backgroundColor: "#585bcc",
          },
        },
      },
    },

    muiTableContainerProps: ({ table }) => ({
      ref: tableContainerRef,
      sx: {
        backgroundColor: "#fff",
        maxHeight: "65vh !important",
        height: "auto",
        overflowY: "auto",
        overflowX: "auto",
      },

      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
      },
      className: "bottom-toolbar-wrapper__new",
    }),
    muiTopToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
        // Optional: Style the inner MuiBox-root (css-wsew38) for visual confirmation
        "& .MuiBox-root.css-wsew38": {
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        },
      },
      className: "top-toolbar-wrapper__new",
    }),
    renderTopToolbarCustomActions: ({ table }) => {
      const selectedRows = table
        .getSelectedRowModel()
        .flatRows.map((r) => r.original);
      const hasSelection = selectedRows.length > 0;

      const Trade_ids = selectedRows
        .map((r) => r.Trade_id || r.trade_id)
        .filter(Boolean);

      const handleExportAll = async () => {
        try {
          const res = await axiosInstance.get(
            "/paper-app/api-pending-trades/export-csv/",
            { responseType: "blob" }
          );
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "pending_trades.csv");
          document.body.appendChild(link);
          link.click();
          link.remove();
        } catch (error) {
          console.error("Export all failed:", error);
        }
      };

      const handleExportSelected = async () => {
        if (Trade_ids.length === 0) {
          alert("No valid Trade IDs found in selected rows.");
          return;
        }

        try {
          const res = await axiosInstance.post(
            "/paper-app/api/api-pending-trades/select-export/",
            { Trade_ids },
            { responseType: "blob" }
          );
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "selected_trades.csv");
          document.body.appendChild(link);
          link.click();
          link.remove();
        } catch (error) {
          console.error("Selected export failed:", error);
        }
      };

      return (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {/* Primary Action */}
          <Button
            variant="text"
            size="small"
            startIcon={<CheckCircle fontSize="small" />}
            onClick={handleOpenConfirm}
          >
            Process
          </Button>

          {/* Bulk Actions */}
          {hasSelection && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => handleBulkEdit(selectedRows)}
              >
                Edit Selected
              </Button>

              <Button
                variant="text"
                color="error"
                onClick={() => {
                  const ids = selectedRows.map((r: any) => r?.original?.Trade_id || r?.Trade_id || r?.id);
                  handleOpenBulkDeleteDialog(ids);
                }}
                startIcon={<DeleteIcon htmlColor="#d32f2f" />}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  color: "#d32f2f !important", // ← FORCES the red text 
                  px: 2, "&:hover": { backgroundColor: "rgba(255,0,0,0.1)" },
                }} > Delete Selected </Button>

            </Box>
          )}

          {/* Utility Actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => {
                table.resetRowSelection();
                table.resetColumnFilters();
                table.resetGlobalFilter();
                table.resetSorting();
                table.resetPagination();
                table.resetColumnVisibility();
                table.resetColumnOrder();
                table.resetGrouping();
                table.resetExpanded();
                table.resetColumnPinning();
              }}
            >
              Reset
            </Button>

            {hasSelection && (
              <Button
                size="small"
                startIcon={<Download fontSize="small" />}
                onClick={handleExportSelected}
              >
                Export Selected
              </Button>
            )}

            <Tooltip title="Export All">
              <IconButton size="small" onClick={handleExportAll}>
                <Download fontSize="small" />
                Export All
              </IconButton>
            </Tooltip>

            <Tooltip title="Logs">
              <IconButton size="small" onClick={handleOpenLogs}>
                <ArticleIcon fontSize="small" />
                Log
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      );
    },
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div
      className="page-body"
      style={{
        paddingTop: 0,
        marginTop: 0,
        width: "100%",
        overflowX: "hidden",
      }}
    >
      <div
        className="container-fluid"
        style={{
          paddingTop: 0,
          marginTop: 0,
          width: "100%",
          overflowX: "hidden",
        }}
      >
        <Row>
          <div className="table-ad">
            <Accordion defaultActiveKey="0" className="table-ad__addInputs">
              <Accordion.Item eventKey="0">
                <Accordion.Header className="accordion_button">
                  Exchange Trades
                </Accordion.Header>
              </Accordion.Item>
            </Accordion>
            <div
              ref={tableContainerRef}
              style={{
                height: "80vh",
                maxHeight: "80vh",
                overflowY: "auto",
                position: "relative",
              }}
            >
              <div className="table-ad__wrapper tablePaperPosition">
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />
                </div>
              </div>
            </div>
          </div>
        </Row>

        {/* Modal */}
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="edit-modal-title"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 700,
              bgcolor: "#f9fafc",
              borderRadius: 2,
              boxShadow: "0px 8px 30px rgba(0,0,0,0.2)",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "#e0f7fa",
                px: 3,
                py: 1.5,
                color: "#212529",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "1rem",
                  color: "#052c65",
                  letterSpacing: 0.3,
                }}
              >
                {editingRows.length > 1 ? "Bulk Edit Trades" : "Edit Trade"}
              </Typography>
              <IconButton
                size="small"
                onClick={handleCloseModal}
                sx={{
                  color: "#052c65",
                  "&:hover": { backgroundColor: "rgba(0,96,100,0.08)" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            {/* Form Grid */}
            <Box
              sx={{
                p: 3,
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 2.5,
                alignItems: "center",
                bgcolor: "#f9fafc",
              }}
            >
              {[
                {
                  label: "Book",
                  value: formData.book,
                  options: books,
                  key: "book",
                },
                {
                  label: "Strategy",
                  value: formData.strategy,
                  options: strategies,
                  key: "strategy",
                },
                {
                  label: "Price",
                  value: formData.price,
                  type: "number",
                  key: "price",
                },
                {
                  label: "Broker",
                  value: formData.broker,
                  options: brokers,
                  key: "broker",
                },
              ].map((field) => (
                <Box key={field.key}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "#263238",
                      mb: 0.5,
                      textAlign: "left",
                    }}
                  >
                    {field.label}
                  </Typography>

                  {/* ✅ Handle numeric and dropdown fields */}
                  {field.type === "number" ? (
                    <TextField
                      size="small"
                      type="number"
                      value={
                        touched[field.key as keyof FormData] ? field.value : ""
                      }
                      onChange={(e) => {
                        setTouched({ ...touched, [field.key]: true });
                        setFormData({
                          ...formData,
                          [field.key]: e.target.value,
                        });
                      }}
                      placeholder={`Enter ${field.label}`}
                      sx={compactInputSx}
                    />
                  ) : (
                    <Select
                      size="small"
                      displayEmpty
                      value={
                        touched[field.key as keyof FormData] ? field.value : ""
                      }
                      onChange={(e) => {
                        setTouched({ ...touched, [field.key]: true });
                        setFormData({
                          ...formData,
                          [field.key]: e.target.value,
                        });
                      }}
                      sx={compactInputSx}
                    >
                      <MenuItem value="">
                        <em>Select {field.label}</em>
                      </MenuItem>

                      {field.options?.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Box>
              ))}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                px: 3,
                py: 2,
                bgcolor: "#f1f3f6",
              }}
            >
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  textTransform: "none",
                  px: 5,
                  py: 1,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  borderRadius: 1,
                  backgroundColor: "#009db5",
                  boxShadow: "0px 3px 6px rgba(0,0,0,0.15)",
                  ":hover": { backgroundColor: "#008699" },
                }}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Modal>
        <Dialog
          open={openLogModal}
          onClose={() => setOpenLogModal(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={maximized}
          PaperProps={{
            sx: {
              background: "#ffffff",
              color: "#333",
              height: maximized ? "100vh" : minimized ? "150px" : "auto",
              width: maximized ? "100vw" : "auto",
              maxHeight: "100vh",
              maxWidth: "100vw",
              margin: 0,
              borderRadius: maximized ? 0 : "10px",
              transition: "all 0.2s ease",
              boxShadow: "0px 4px 14px rgba(0,0,0,0.15)",
            },
          }}
        >
          {/* HEADER */}
          <DialogTitle
            sx={{
              fontWeight: 600,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#f5f5f5",
              borderBottom: "1px solid #ddd",
              py: 1,
              px: 2,
              fontSize: "0.95rem",
              color: "#333",
            }}
          >
            API Logs
            {/* ACTION BUTTONS */}
            <Box sx={{ display: "flex", gap: 1 }}>
              {/* MAXIMIZE */}
              <IconButton
                size="small"
                onClick={() => {
                  setMaximized(!maximized);
                  setMinimized(false);
                }}
                sx={{
                  color: "#555",
                  "&:hover": { background: "rgba(0,0,0,0.06)" },
                }}
              >
                {maximized ? (
                  <FilterNoneIcon fontSize="small" />
                ) : (
                  <CheckBoxOutlineBlankIcon fontSize="small" />
                )}
              </IconButton>

              {/* CLOSE */}
              <IconButton
                size="small"
                onClick={() => setOpenLogModal(false)}
                sx={{
                  color: "#555",
                  "&:hover": {
                    background: "rgba(255,0,0,0.18)",
                    color: "#d32f2f",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>

          {/* CONTENT */}
          {!minimized && (
            <DialogContent dividers sx={{ p: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  height: maximized ? "calc(100vh - 48px)" : "70vh",
                }}
              >
                {/* LEFT SIDEBAR */}
                <Box
                  sx={{
                    width: 150,
                    borderRight: "1px solid #ddd",
                    background: "#fafafa",
                    display: "flex",
                    flexDirection: "column",
                    p: 1,
                    gap: 0.5,
                  }}
                >
                  {["ICE", "TT"].map((file) => (
                    <Box
                      key={file}
                      onClick={() => handleTabChange(file)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.7,
                        px: 1,
                        py: 0.6,
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.78rem",
                        background:
                          activeSource === file ? "#e3f2fd" : "transparent",
                        color: activeSource === file ? "#1976d2" : "#555",
                        "&:hover": { background: "#eeeeee" },
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 16 }} />
                      {file}.log
                    </Box>
                  ))}
                </Box>

                {/* RIGHT PANEL */}
                <Box
                  sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
                >
                  {/* SEARCH */}
                  <Box
                    sx={{
                      p: 1,
                      borderBottom: "1px solid #ddd",
                      background: "#fafafa",
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder="Search logs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        width: 220,
                        "& input": {
                          fontSize: "0.75rem",
                          padding: "5px 8px",
                        },
                      }}
                      InputProps={{
                        sx: {
                          background: "#fff",
                          borderRadius: 1,
                          height: "30px",
                        },
                      }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleExportLogs}
                      startIcon={<FileDownloadIcon />}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.75rem",
                        ml: 1,
                        height: "30px",
                        borderColor: "#ddd",
                        color: "#555",
                        "&:hover": {
                          borderColor: "#1976d2",
                          color: "#1976d2",
                          background: "rgba(25, 118, 210, 0.04)",
                        },
                      }}
                    >
                      Export
                    </Button>
                  </Box>

                  {/* LOG VIEWER */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      background: "#ffffff",
                      color: "#333",
                      fontFamily: "monospace",
                      fontSize: "0.80rem",
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.4,
                      p: 2,
                    }}
                  >
                    {filteredLogs}
                  </Box>
                </Box>
              </Box>
            </DialogContent>
          )}
        </Dialog>

        <Dialog
          open={confirmOpen}
          keepMounted
          onClose={handleCloseConfirm}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              p: 1,
              background: "linear-gradient(145deg, #f9f9f9, #ffffff)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              fontSize: "1.05rem",
              color: "#0d47a1",
              textAlign: "center",
              pb: 0,
            }}
          >
            {processing ? "Processing Trades" : "Confirm Processing"}
          </DialogTitle>

          <DialogContent>
            {!processResult ? (
              // Initial confirmation view
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: "#455a64",
                  textAlign: "center",
                  mt: 1,
                }}
              >
                Are you sure you want to process the selected trades?
              </Typography>
            ) : (
              // Results view
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor:
                      finalStatus === "success" ? "#e8f5e9" : "#ffebee",
                    border: `1px solid ${finalStatus === "success" ? "#4caf50" : "#f44336"
                      }`,

                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color:
                        finalStatus === "success"
                          ? "#2e7d32"
                          : "#c62828",
                      mb: 1,
                    }}
                  >
                    {finalStatus === "success" ? "Success" : "Failed"}
                  </Typography>


                  <Typography sx={{ fontSize: "0.85rem", color: "#333", mb: 1.5 }}>
                    {processResult.message}
                  </Typography>

                  {processResult.created.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: "#2e7d32",
                          mb: 0.5,
                        }}
                      >
                        Processed ({processResult.created.length}):
                      </Typography>

                      <Box
                        sx={{
                          maxHeight: "120px",
                          overflowY: "auto",
                          fontSize: "0.75rem",
                          color: "#455a64",
                          pl: 2,
                        }}
                      >
                        {processResult.created.map((item, idx) => (
                          <Typography key={idx} sx={{ fontSize: "0.75rem" }}>
                            • {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {processResult.skipped.length > 0 && (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: "#e65100",
                          mb: 0.5,
                        }}
                      >
                        Skipped ({processResult.skipped.length}):
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: "120px",
                          overflowY: "auto",
                          fontSize: "0.75rem",
                          color: "#455a64",
                          pl: 2,
                        }}
                      >
                        {processResult.skipped.map((item, idx) => (
                          <Typography key={idx} sx={{ fontSize: "0.75rem" }}>
                            • {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {processing && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", pb: 1.5, pt: 0.5, gap: 2 }}>
            {!processResult ? (
              <>
                <Button
                  onClick={handleCloseConfirm}
                  disabled={processing}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8rem",
                    borderRadius: "8px",
                    px: 2,
                    py: 0.6,
                    backgroundColor: "#eceff1",
                    color: "#37474f",
                    "&:hover": { backgroundColor: "#cfd8dc" },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  onClick={handleConfirmProcess}
                  variant="contained"
                  disabled={processing}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8rem",
                    borderRadius: "8px",
                    px: 2,
                    py: 0.6,
                    background: "linear-gradient(135deg, #00bfa5 0%, #009db5 100%)",
                    color: "#fff",
                    "&:hover": {
                      background: "linear-gradient(135deg, #00a48c 0%, #008ca3 100%)",
                    },
                  }}
                >
                  {processing ? <CircularProgress size={22} /> : "Yes, Process"}
                </Button>
              </>
            ) : (
              <Button
                onClick={handleCloseConfirm}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontSize: "0.8rem",
                  borderRadius: "8px",
                  px: 3,
                  py: 0.6,
                  background: "linear-gradient(135deg, #00bfa5 0%, #009db5 100%)",
                  color: "#fff",
                  "&:hover": {
                    background: "linear-gradient(135deg, #00a48c 0%, #008ca3 100%)",
                  },
                }}
              >
                Close
              </Button>
            )}
          </DialogActions>
        </Dialog>
        <Dialog
          open={openBulkDeleteDialog}
          onClose={handleCloseBulkDeleteDialog}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              p: 1,
              background: "linear-gradient(145deg, #f9f9f9, #ffffff)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
            Confirm Bulk Delete
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="black">
              Are you sure you want to delete {bulkDeleteIds.length} selected
              trades?
              <br />
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseBulkDeleteDialog}
              sx={{ color: "#ccc", textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmBulkDelete}
              color="error"
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 500,
                px: 2.5,
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openRawModal}
          onClose={handleCloseRawModal}
          maxWidth={false}
          hideBackdrop
          disableEnforceFocus
          PaperComponent={isMaximized ? Paper : DraggablePaper}
          PaperProps={{
            sx: {
              width: isMaximized ? "100vw" : "420px",
              height: isMaximized ? "100vh" : "500px",
              maxHeight: "100vh",
              maxWidth: "100vw",
              backgroundColor: "#fff",
              borderRadius: isMaximized ? "0" : "8px",
              position: "fixed",
              right: isMaximized ? 0 : "16px",
              left: isMaximized ? 0 : "auto",
              bottom: isMaximized ? 0 : "auto",
              m: 0,
              boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
              transition: "all 0.2s ease",
            },
          }}
          sx={{
            pointerEvents: "none",
            "& .MuiDialog-paper": {
              pointerEvents: "auto",
            },
          }}
        >
          <DialogTitle
            id="draggable-dialog-title"
            sx={{
              fontWeight: 600,
              borderBottom: "1px solid #eee",
              backgroundColor: "#f9f9f9",
              color: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pr: 1,
              cursor: isMaximized ? "default" : "move",
              userSelect: "none",
              py: 1,
              fontSize: "14px",
            }}
          >
            Raw XML
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title={isMaximized ? "Restore" : "Maximize"}>
                <IconButton
                  onClick={() => setIsMaximized((prev) => !prev)}
                  size="small"
                  sx={{ color: "#000" }}
                >
                  {isMaximized ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton onClick={handleCloseRawModal} size="small" sx={{ color: "#000" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              backgroundColor: "#fff",
              color: "#000",
              overflow: "auto",
              p: 1.5,
            }}
          >
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "12px",
                lineHeight: "1.5",
                fontFamily: "monospace",
                margin: 0,
              }}
            >
              {selectedRawMessage}
            </pre>
          </DialogContent>
        </Dialog>

        {/* Toast */}
        <Snackbar
          open={toastOpen}
          autoHideDuration={3000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="success" sx={{ width: "100%" }}>
            Changes saved successfully!
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default IceExhcnageTrades;
